import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

interface ListSummary {
  id: number;
  title: string;
  description: string;
  is_public: boolean;
  slug: string;
  item_count: number;
  created_at: string | null;
}

interface ListItem {
  id: number;
  item_type: string;
  added_at: string | null;
  note?: string;
  note_public?: boolean;
  article?: { id: number; title: string; slug: string; publisher: string; link: string };
  feed?: { id: number; name: string; impact_factor: number | null };
  category?: { id: number; name: string; slug: string };
}

interface ListDetail {
  id: number;
  title: string;
  description: string;
  is_public: boolean;
  slug: string;
  owner: string;
  is_owner: boolean;
  items: ListItem[];
}

// ---- Bookmark Lists Page (user's own lists) ----

export function BookmarkListsPage({ token }: { token: string }) {
  const [lists, setLists] = useState<ListSummary[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newPublic, setNewPublic] = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchLists = () => {
    fetch('/api/lists', { headers }).then(r => r.json()).then(setLists);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchLists, [token]);

  const createList = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch('/api/lists', {
      method: 'POST', headers,
      body: JSON.stringify({ title: newTitle, description: newDesc, is_public: newPublic }),
    });
    setNewTitle(''); setNewDesc(''); setNewPublic(false); setShowNew(false);
    fetchLists();
  };

  const deleteList = async (id: number) => {
    await fetch(`/api/lists/${id}`, { method: 'DELETE', headers });
    fetchLists();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>my lists</h2>
        <button onClick={() => setShowNew(!showNew)}
          style={{ background: '#5f99cf', color: 'white', border: 'none', padding: '3px 10px', cursor: 'pointer', fontSize: 11 }}>
          {showNew ? 'cancel' : '+ new list'}
        </button>
      </div>

      {showNew && (
        <form onSubmit={createList} style={{ marginBottom: 15, padding: 10, border: '1px solid #5f99cf', background: '#eff7ff' }}>
          <div style={{ marginBottom: 5 }}>
            <input placeholder="list title" value={newTitle} onChange={e => setNewTitle(e.target.value)} required
              style={{ width: '100%', padding: '3px 5px', border: '1px solid #5f99cf', fontSize: 11, boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 5 }}>
            <textarea placeholder="description (optional)" value={newDesc} onChange={e => setNewDesc(e.target.value)}
              style={{ width: '100%', height: 40, padding: '3px 5px', border: '1px solid #5f99cf', fontSize: 11, boxSizing: 'border-box' }} />
          </div>
          <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5 }}>
            <input type="checkbox" checked={newPublic} onChange={e => setNewPublic(e.target.checked)} />
            public (shareable by URL)
          </label>
          <button type="submit" style={{ background: '#5f99cf', color: 'white', border: 'none', padding: '3px 10px', cursor: 'pointer', fontSize: 11 }}>create</button>
        </form>
      )}

      {lists.length === 0 && !showNew && (
        <p style={{ fontSize: 11, color: '#888' }}>You have no bookmark lists yet.</p>
      )}

      {lists.map(bl => (
        <div key={bl.id} className="bookmark-list-row">
          <div style={{ flex: 1 }}>
            <Link to={`/jf/list/${bl.slug}`} style={{ fontWeight: 'bold', fontSize: 13 }}>{bl.title}</Link>
            {bl.description && <div style={{ fontSize: 11, color: '#888' }}>{bl.description}</div>}
            <div style={{ fontSize: 10, color: '#aaa' }}>
              {bl.item_count} items | {bl.is_public ? 'public' : 'private'}
            </div>
          </div>
          <button onClick={() => deleteList(bl.id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 11 }}>delete</button>
        </div>
      ))}
    </div>
  );
}


// ---- Public List View ----

export function PublicListView({ token }: { token: string | null }) {
  const { slug } = useParams();
  const [list, setList] = useState<ListDetail | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    fetch(`/api/lists/${slug}`, { headers })
      .then(r => { if (!r.ok) throw new Error('Not found or private'); return r.json(); })
      .then(setList)
      .catch(e => setError(e.message));
  }, [slug, token]);

  const removeItem = async (itemId: number) => {
    if (!list || !token) return;
    await fetch(`/api/lists/${list.id}/items/${itemId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setList({ ...list, items: list.items.filter(i => i.id !== itemId) });
  };

  if (error) return <div style={{ padding: 20, color: '#888' }}>{error}</div>;
  if (!list) return <div style={{ padding: 20 }}>Loading...</div>;

  const articles = list.items.filter(i => i.item_type === 'article');
  const feeds = list.items.filter(i => i.item_type === 'feed');
  const topics = list.items.filter(i => i.item_type === 'category');

  return (
    <div>
      <h2 style={{ fontSize: 16, marginBottom: 2 }}>{list.title}</h2>
      {list.description && <p style={{ fontSize: 11, color: '#888', marginTop: 0 }}>{list.description}</p>}
      <div style={{ fontSize: 10, color: '#aaa', marginBottom: 15 }}>
        by {list.owner} | {list.is_public ? 'public' : 'private'} | {list.items.length} items
      </div>

      {topics.length > 0 && (
        <div style={{ marginBottom: 15 }}>
          <div className="sidebar-header" style={{ fontSize: 11, marginBottom: 5 }}>topics</div>
          {topics.map(i => (
            <div key={i.id} className="bookmark-item">
              <Link to={`/${i.category?.slug || ''}`}>{i.category?.name}</Link>
              {list.is_owner && <button onClick={() => removeItem(i.id)} className="btn-remove">x</button>}
            </div>
          ))}
        </div>
      )}

      {feeds.length > 0 && (
        <div style={{ marginBottom: 15 }}>
          <div className="sidebar-header" style={{ fontSize: 11, marginBottom: 5 }}>sources</div>
          {feeds.map(i => (
            <div key={i.id} className="bookmark-item">
              <span>{i.feed?.name}</span>
              {i.feed?.impact_factor && <span style={{ color: '#888', fontSize: 10, marginLeft: 4 }}>IF: {i.feed.impact_factor}</span>}
              {list.is_owner && <button onClick={() => removeItem(i.id)} className="btn-remove">x</button>}
            </div>
          ))}
        </div>
      )}

      {articles.length > 0 && (
        <div style={{ marginBottom: 15 }}>
          <div className="sidebar-header" style={{ fontSize: 11, marginBottom: 5 }}>articles</div>
          {articles.map(i => (
            <div key={i.id} className="bookmark-item">
              <div>
                <Link to={`/jf/article/${i.article?.slug || ''}`}>{i.article?.title}</Link>
                <span className="domain">({i.article?.publisher})</span>
                {i.note && (
                  <div style={{ marginTop: 4, padding: '4px 8px', background: i.note_public ? '#f0f8f0' : '#f8f8f0', borderLeft: `3px solid ${i.note_public ? '#228B22' : '#DAA520'}`, fontSize: 12 }}>
                    {i.note}
                    <span style={{ fontSize: 9, color: '#888', marginLeft: 6 }}>{i.note_public ? 'public' : 'private'}</span>
                  </div>
                )}
              </div>
              {list.is_owner && <button onClick={() => removeItem(i.id)} className="btn-remove">x</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ---- Save-to-List Dropdown (inline on article rows) ----

export function SaveToListDropdown({ articleId, token, onClose }: { articleId: number; token: string; onClose: () => void }) {
  const [lists, setLists] = useState<ListSummary[]>([]);
  const [adding, setAdding] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch('/api/lists', { headers }).then(r => r.json()).then(setLists);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addToList = async (listId: number) => {
    setAdding(listId);
    await fetch(`/api/lists/${listId}/items`, {
      method: 'POST', headers,
      body: JSON.stringify({ item_type: 'article', article_id: articleId }),
    });
    setAdding(null);
    onClose();
  };

  const createAndAdd = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    const res = await fetch('/api/lists', {
      method: 'POST', headers,
      body: JSON.stringify({ title: newTitle.trim() }),
    });
    if (res.ok) {
      const newList = await res.json();
      await addToList(newList.id);
    }
    setCreating(false);
  };

  return (
    <div className="save-dropdown">
      <div style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 5, borderBottom: '1px solid #5f99cf', paddingBottom: 3 }}>
        add to list
      </div>
      {lists.map(bl => (
        <div key={bl.id} className="save-dropdown-item" onClick={() => addToList(bl.id)}>
          {adding === bl.id ? 'adding...' : bl.title}
        </div>
      ))}
      {showCreate ? (
        <div style={{ padding: '4px 0' }}>
          <input
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            placeholder="List name"
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter') createAndAdd(); }}
            style={{ width: '100%', fontSize: 10, padding: '3px 4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
            <div className="save-dropdown-item" onClick={createAndAdd} style={{ color: '#5f99cf', flex: 1, textAlign: 'center' }}>
              {creating ? 'creating...' : 'create & add'}
            </div>
            <div className="save-dropdown-item" onClick={() => setShowCreate(false)} style={{ color: '#888', flex: 1, textAlign: 'center' }}>
              back
            </div>
          </div>
        </div>
      ) : (
        <div className="save-dropdown-item" onClick={() => setShowCreate(true)} style={{ color: '#5f99cf' }}>
          + new list
        </div>
      )}
      <div className="save-dropdown-item" onClick={onClose} style={{ color: '#888' }}>cancel</div>
    </div>
  );
}
